// src/components/SearchModal.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Box,
  Chip,
  InputAdornment,
  Divider,
  useTheme,
  alpha
} from '@mui/material';
import {
  Search,
  Folder,
  Event,
  Person,
  Inventory2,
  LocationOn,
  EventNote,
  Description,
  KeyboardArrowDown,
  KeyboardArrowUp
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from '../hooks/useDebounce';
import axiosInstance from '../utils/axiosConfig';

interface SearchResult {
  id: string;
  title: string;
  subtitle: string;
  type: 'resource' | 'performance' | 'personnel' | 'inventory' | 'venue' | 'event' | 'contract';
  url: string;
  date: string;
  status: string;
  metadata: Record<string, any>;
}

interface SearchModalProps {
  open: boolean;
  onClose: () => void;
}

const SearchModal: React.FC<SearchModalProps> = ({ open, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const debouncedQuery = useDebounce(query, 300);
  const navigate = useNavigate();
  const theme = useTheme();

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
      setSelectedIndex(0);
    }
  }, [open]);

  // Type icons
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'resource': return <Folder fontSize="small" />;
      case 'performance': return <Event fontSize="small" />;
      case 'personnel': return <Person fontSize="small" />;
      case 'inventory': return <Inventory2 fontSize="small" />;
      case 'venue': return <LocationOn fontSize="small" />;
      case 'event': return <EventNote fontSize="small" />;
      case 'contract': return <Description fontSize="small" />;
      default: return <Search fontSize="small" />;
    }
  };

  // Type colors
  const getTypeColor = (type: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (type) {
      case 'resource': return 'primary';
      case 'performance': return 'secondary';
      case 'personnel': return 'info';
      case 'inventory': return 'warning';
      case 'venue': return 'success';
      case 'event': return 'error';
      case 'contract': return 'default';
      default: return 'default';
    }
  };

  // Search function
  useEffect(() => {
    const performSearch = async () => {
      if (debouncedQuery.length < 2) {
        setResults([]);
        setSelectedIndex(0);
        return;
      }

      setLoading(true);
      try {
        const response = await axiosInstance.get(`/api/search/global?q=${encodeURIComponent(debouncedQuery)}&limit=20`);
        setResults(response.data);
        setSelectedIndex(0);
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    performSearch();
  }, [debouncedQuery]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!open) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
          break;
        case 'ArrowUp':
          event.preventDefault();
          setSelectedIndex(prev => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          event.preventDefault();
          if (results[selectedIndex]) {
            handleResultClick(results[selectedIndex]);
          }
          break;
        case 'Escape':
          event.preventDefault();
          onClose();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, results, selectedIndex, onClose]);

  // Handle result click
  const handleResultClick = (result: SearchResult) => {
    navigate(result.url);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          position: 'fixed',
          top: '10%',
          margin: 0,
          borderRadius: 2,
          maxHeight: '80vh'
        }
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Search Input */}
        <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <TextField
            fullWidth
            placeholder="Search everything..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  border: 'none'
                },
                fontSize: '1.1rem'
              }
            }}
          />
        </Box>

        {/* Results */}
        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
          {loading ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                Searching...
              </Typography>
            </Box>
          ) : query.length < 2 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                Start typing to search
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Search across performances, resources, personnel, inventory, and more
              </Typography>
            </Box>
          ) : results.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No results found for "{query}"
              </Typography>
            </Box>
          ) : (
            <List dense sx={{ py: 0 }}>
              {results.map((result, index) => (
                <ListItem
                  key={result.id}
                  button
                  selected={index === selectedIndex}
                  onClick={() => handleResultClick(result)}
                  sx={{
                    backgroundColor: index === selectedIndex 
                      ? alpha(theme.palette.primary.main, 0.1)
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.05)
                    },
                    py: 1.5
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {getTypeIcon(result.type)}
                  </ListItemIcon>
                  
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                          {result.title}
                        </Typography>
                        <Chip
                          label={result.type}
                          size="small"
                          color={getTypeColor(result.type)}
                          sx={{ height: 18, fontSize: '0.7rem' }}
                        />
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        {result.subtitle}
                      </Typography>
                    }
                  />

                  {index === selectedIndex && (
                    <Box sx={{ ml: 2, color: 'text.secondary' }}>
                      <Typography variant="caption">
                        ↵ to select
                      </Typography>
                    </Box>
                  )}
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        {/* Footer */}
        {results.length > 0 && (
          <>
            <Divider />
            <Box sx={{ p: 2, backgroundColor: 'grey.50' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary">
                  {results.length} results found
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <KeyboardArrowUp fontSize="small" />
                    <KeyboardArrowDown fontSize="small" />
                    navigate
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ↵ select
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    esc close
                  </Typography>
                </Box>
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SearchModal;